import dayjs from 'dayjs';
import { ValidationObserver, ValidationProvider, extend } from 'vee-validate';
import { email, required } from 'vee-validate/dist/rules';
import { defineComponent } from 'vue';
import { isStreetNumberFormat } from '@/shared/helpers/is-street-number-format';
import { decodeHtml } from '@/shared/helpers/decode';
import i18n from '@/i18n';

export const postalZipRegex = new RegExp(/^\s*[0-9]{5}\s*$|^\s*[A-Z][0-9][A-Z] ?[0-9][A-Z][0-9]\s*$/, 'i');

export const methods = {
  isImageName(fileName: string) {
    return !!(fileName?.match(/.(jpg|jpeg|png|gif|jfif|webp)$/i));
  },

  isPDFName(fileName: string) {
    return fileName?.slice(-3) === 'pdf';
  },

  isEmail(text: string): boolean {
    // eslint-disable-next-line max-len, vue/max-len
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(text).toLowerCase());
  },

  isPhone(text: string): boolean {
    return [10, 11, 12, 13].includes(text.match(/\d/g)?.length || 0);
  },

  isURL(text: string): boolean {
    const pattern = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]{1,63}(\.[a-zA-Z0-9-]{2,})+([\/?#].*)?$/;
    const trimmed = text.trim();
    return trimmed.length > 0 && pattern.test(trimmed);
  },

  newPassword(password: string): boolean {
    if (password.length < 8) {
      return false;
    }

    const passwordTests = [
      /[a-z]/, // Lowercase
      /[A-Z]/, // Uppercase
      /.{20,}/, // 20+ Characters
      /[\W_]/, // Any symbol (unicode included)
      /[\d]/, // Any digit
    ];

    return passwordTests
      .map((reg) => reg.test(password))
      .filter((test) => test)
      .length >= 3;
  },
};

export const isPasswordValid = (password: string) => {
  const isValid = methods.newPassword(password);
  return {
    isValid,
    message: !isValid
      ? 'New passwords must have a minimum of 8 characters, a mix of lowercase and uppercase characters, and a symbol or number.'
      : '',
  };
};

extend('required', { ...required, message: () => i18n.t('validation.fieldRequired') as string });
extend('email', email);
extend('url', { validate: methods.isURL, message: 'Url is invalid' });

extend('fieldExists', {
  validate(value: string, args: any) {
    return args.fieldKeys.split('_').includes(value);
  },
  params: ['fieldKeys'],
  message: 'Conditional field no longer exists! Please select another.',
});

extend('minDate', {
  validate: (day: string, { minDate }: Record<string, any>) => {
    const d = dayjs(new Date(day));

    if (!d.isValid() || minDate === '' || minDate === '0000-00-00') return true;

    return d.isAfter(minDate, 'day');
  },
  params: ['minDate'],
  message: 'End Date cannot be before than the Start Date.',
});

extend('maxDate', {
  validate: (day: string, { maxDate }: Record<string, any>) => {
    const d = dayjs(new Date(day));

    if (!d.isValid() || maxDate === '' || maxDate === '0000-00-00') return true;

    return d.isBefore(maxDate, 'day');
  },
  params: ['maxDate'],
  message: 'Start Date cannot be greater than the End Date.',
});

extend('isBetweenMinAndMax', {
  validate(value: string[], { min, max }: Record<string, any>) {
    return value.length >= Number(min) && value.length <= Number(max);
  },
  params: ['min', 'max'],
  message: 'Number of selected values cannot go below minimum and above maximum',
});

extend('duplicateValues', {
  validate: (values: string[]|string) => {
    if (values.length <= 1 || values === '') return true;
    const inputValues = typeof values === 'string' ? values.split('\n') : values;
    return !inputValues.some((value, index) => {
      const lowerCaseValue = value.toLowerCase();
      return index !== inputValues.findIndex((v) => v.toLowerCase() === lowerCaseValue);
    });
  },
  message: 'Duplicate values are not valid.',
});

extend('incompletePrimaryAddress', {
  validate: (_value: string, { isAddressComplete }: Record<string, any>) => isAddressComplete,
  message: 'Address must be complete',
});

extend('streetNumberFormat', {
  validate: (value: string) => isStreetNumberFormat(value),
  message: 'Street # must be valid',
});

extend('isValidLatitude', {
  validate: (value: string) => (/^([-+]?([1-8]?[0-9](\.\d+)?|90(\.0+)?))$/).test(value),
  message: 'Latitude must be between -90 and 90, with optional decimals (ex. -89.5).',
});

extend('isValidLongitude', {
  validate: (value: string) => (/^([-+]?((1[0-7][0-9]|[1-9]?[0-9])(\.\d+)?|180(\.0+)?))$/).test(value),
  message: 'Longitude must be between -180 and 180, with optional decimals (ex. -179.5).',
});

export default defineComponent({
  components: {
    ValidationProvider,
    ValidationObserver,
  },
  methods,
});
